import React, { useEffect, useState } from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/UI/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { Alert, AlertDescription } from '../components/UI/Alert';
import { Input } from '../components/UI/Input';
import { Label } from '../components/UI/Label';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { getItsmConfig, saveItsmConfig, ItsmConfig } from '../services/integrations';
import toast from 'react-hot-toast';

const MASKED = '********';

const StatusChip: React.FC<{ configured: boolean }> = ({ configured }) =>
  configured ? (
    <span className='inline-flex items-center gap-1 rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-800 dark:bg-success-900/40 dark:text-success-300'>
      <CheckCircleIcon className='h-3.5 w-3.5' />
      Configurado
    </span>
  ) : (
    <span className='inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400'>
      <XCircleIcon className='h-3.5 w-3.5' />
      Não configurado
    </span>
  );

export const Integrations: React.FC = () => {
  const [config, setConfig] = useState<ItsmConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Jira form fields
  const [jiraUrl, setJiraUrl] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraToken, setJiraToken] = useState('');
  const [jiraProject, setJiraProject] = useState('SEC');
  const [showJiraToken, setShowJiraToken] = useState(false);

  // ServiceNow form fields
  const [snowInstance, setSnowInstance] = useState('');
  const [snowUser, setSnowUser] = useState('');
  const [snowPassword, setSnowPassword] = useState('');
  const [snowTable, setSnowTable] = useState('incident');
  const [showSnowPassword, setShowSnowPassword] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await getItsmConfig();
      setConfig(data);
      setJiraUrl(data.jira.url ?? '');
      setJiraEmail(data.jira.email ?? '');
      setJiraToken(data.jira.api_token ?? '');
      setJiraProject(data.jira.project_key ?? 'SEC');
      setSnowInstance(data.servicenow.instance ?? '');
      setSnowUser(data.servicenow.user ?? '');
      setSnowPassword(data.servicenow.password ?? '');
      setSnowTable(data.servicenow.table ?? 'incident');
    } catch {
      toast.error('Erro ao carregar configurações de integrações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await saveItsmConfig({
        jira: {
          url: jiraUrl || null,
          email: jiraEmail || null,
          api_token: jiraToken || null,
          project_key: jiraProject || 'SEC',
        },
        servicenow: {
          instance: snowInstance || null,
          user: snowUser || null,
          password: snowPassword || null,
          table: snowTable || 'incident',
        },
      });
      setConfig(updated);
      toast.success('Configurações salvas com sucesso');
    } catch {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center mt-16'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title='Integrações'
        subtitle='Configure sistemas externos de ITSM para sincronização de tickets'
      />

      <Alert className='mb-6'>
        <AlertDescription>
          Os tokens e senhas são armazenados de forma segura. Ao editar, deixe o campo como{' '}
          <strong>{MASKED}</strong> para manter o valor atual, ou preencha com o novo valor.
        </AlertDescription>
      </Alert>

      <div className='flex flex-wrap gap-6'>
        {/* Jira */}
        <div className='flex-1 min-w-[400px]'>
          <Card>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <CardTitle className='text-lg'>Jira</CardTitle>
                {config && <StatusChip configured={config.jira_configured} />}
              </div>
              <p className='text-sm text-gray-500 dark:text-gray-400'>Atlassian Jira Cloud ou Server</p>
            </CardHeader>
            <CardContent className='flex flex-col gap-4'>
              <div className='space-y-1.5'>
                <Label htmlFor='jira-url'>URL da instância</Label>
                <Input
                  id='jira-url'
                  placeholder='https://yourorg.atlassian.net'
                  value={jiraUrl}
                  onChange={e => setJiraUrl(e.target.value)}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='jira-email'>E-mail da conta de serviço</Label>
                <Input
                  id='jira-email'
                  placeholder='service@yourorg.com'
                  value={jiraEmail}
                  onChange={e => setJiraEmail(e.target.value)}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='jira-token'>API Token</Label>
                <div className='relative'>
                  <Input
                    id='jira-token'
                    value={jiraToken}
                    onChange={e => setJiraToken(e.target.value)}
                    type={showJiraToken ? 'text' : 'password'}
                    className='pr-10'
                  />
                  <button
                    type='button'
                    className='absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                    onClick={() => setShowJiraToken(v => !v)}
                  >
                    {showJiraToken ? <EyeSlashIcon className='h-4 w-4' /> : <EyeIcon className='h-4 w-4' />}
                  </button>
                </div>
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Gere em{' '}
                  <a
                    href='https://id.atlassian.com/manage-profile/security/api-tokens'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-primary-600 hover:underline dark:text-primary-400'
                  >
                    id.atlassian.com
                  </a>
                </p>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='jira-project'>Chave do projeto</Label>
                <Input
                  id='jira-project'
                  placeholder='SEC'
                  value={jiraProject}
                  onChange={e => setJiraProject(e.target.value)}
                />
                <p className='text-xs text-gray-500 dark:text-gray-400'>Prefixo das issues (ex: SEC, IT, CTI)</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ServiceNow */}
        <div className='flex-1 min-w-[400px]'>
          <Card>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <CardTitle className='text-lg'>ServiceNow</CardTitle>
                {config && <StatusChip configured={config.servicenow_configured} />}
              </div>
              <p className='text-sm text-gray-500 dark:text-gray-400'>ServiceNow ITSM (REST API)</p>
            </CardHeader>
            <CardContent className='flex flex-col gap-4'>
              <div className='space-y-1.5'>
                <Label htmlFor='snow-instance'>Instância</Label>
                <Input
                  id='snow-instance'
                  placeholder='yourorg.service-now.com'
                  value={snowInstance}
                  onChange={e => setSnowInstance(e.target.value)}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='snow-user'>Usuário de integração</Label>
                <Input
                  id='snow-user'
                  value={snowUser}
                  onChange={e => setSnowUser(e.target.value)}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='snow-password'>Senha</Label>
                <div className='relative'>
                  <Input
                    id='snow-password'
                    value={snowPassword}
                    onChange={e => setSnowPassword(e.target.value)}
                    type={showSnowPassword ? 'text' : 'password'}
                    className='pr-10'
                  />
                  <button
                    type='button'
                    className='absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
                    onClick={() => setShowSnowPassword(v => !v)}
                  >
                    {showSnowPassword ? <EyeSlashIcon className='h-4 w-4' /> : <EyeIcon className='h-4 w-4' />}
                  </button>
                </div>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='snow-table'>Tabela de destino</Label>
                <Input
                  id='snow-table'
                  placeholder='incident'
                  value={snowTable}
                  onChange={e => setSnowTable(e.target.value)}
                />
                <p className='text-xs text-gray-500 dark:text-gray-400'>
                  Ex: incident, sc_request, u_security_incident
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <hr className='my-6 border-gray-200 dark:border-gray-700' />

      <div className='flex justify-end'>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <LoadingSpinner size='sm' className='mr-2' />}
          {saving ? 'Salvando…' : 'Salvar configurações'}
        </Button>
      </div>
    </div>
  );
};

export default Integrations;
